"use client";

/**
 * useAbandonmentTracking Hook
 *
 * Uses the Visibility API to detect when users leave the setup flow tab.
 * If a user returns within 30 minutes, the session is resumed (abandonment cleared).
 * If they return after 30+ minutes, the abandonment stands.
 *
 * Call `stopTracking()` when user completes the setup flow to prevent
 * tracking visibility changes after completion.
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useCallback, useRef } from "react";
const REENGAGEMENT_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

interface UseAbandonmentTrackingReturn {
  /** Stop tracking visibility changes (call on setup completion) */
  stopTracking: () => void;
}
export function useAbandonmentTracking() {
  const $ = _c(4);
  const blurTimeRef = useRef(null);
  const isTrackingRef = useRef(true);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      isTrackingRef.current = false;
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const stopTracking = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleVisibilityChange = async () => {
        if (!isTrackingRef.current) {
          return;
        }
        if (document.hidden) {
          blurTimeRef.current = Date.now();
          ;
          try {
            await fetch("/api/setup/session", {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json"
              },
              credentials: "include",
              body: JSON.stringify({
                action: "visibility_blur"
              })
            });
          } catch (t3) {
            const error = t3;
            console.error("[AbandonmentTracking] Blur tracking failed:", error);
          }
        } else {
          const blurDuration = blurTimeRef.current ? Date.now() - blurTimeRef.current : 0;
          if (blurDuration > 0 && blurDuration < REENGAGEMENT_THRESHOLD_MS) {
            ;
            try {
              await fetch("/api/setup/session", {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify({
                  action: "visibility_focus"
                })
              });
            } catch (t4) {
              const error_0 = t4;
              console.error("[AbandonmentTracking] Focus tracking failed:", error_0);
            }
          }
          blurTimeRef.current = null;
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      stopTracking
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}